/** @jsxImportSource @emotion/react */
import { Interpolation, Theme, css } from "@emotion/react";
import styled from "@emotion/styled";
import { render } from "@testing-library/react";
import { useEffect, useRef, useState } from "react";
import { theme } from "styles/theme";
import Icon from "uis/Icon";
import InputField from "uis/InputField";

type SelectorProps = {
    value?: string;
    data: string[];
    onChange?: (value: string) => void;
    showArrow?: boolean;
    styles?: {
        container?: Interpolation<Theme>;
        text?: Interpolation<Theme>;
        item?: Interpolation<Theme>;
        items?: Interpolation<Theme>;
    };
    allowSearch?: boolean;
    allowMulti?: boolean;
    placeholder?: string;
    RenderInput?: JSX.Element;
    onChangeSelectOpen?: (open: boolean) => void;
};

const SelectContainer = styled.div`
    background-color: white;
    border-radius: 12px;
    filter: drop-shadow(0px 4px 20px rgba(69, 76, 83, 0.15));
    position: absolute;
    top: 50px;
    right: 0;
    width: 100%;
    max-height: 200px;
    min-width: 120px;
    overflow: auto;
    z-index: 1;
    cursor: pointer;
`;

const SelectItem = styled.p<{ selected: boolean }>`
    margin: 12px;
    padding: 12px;
    border-radius: 8px;
    background-color: ${(props) => (props.selected ? theme.neutral50 : "white")};
    font-weight: ${(props) => (props.selected ? 700 : 500)};
    &:hover {
        background-color: ${theme.neutral50};
    }
`;

const InputContainer = styled.button`
    border-radius: 10px;
    width: 150px;
    padding: 12px 12px;
    background-color: ${theme.neutral50};
    display: flex;
    align-items: center;
    border: none;

    &:hover {
        background-color: ${theme.neutral100};
    }
`;

const Select_V2 = (props: SelectorProps) => {
    const {
        value,
        data,
        onChange = () => {},
        styles,
        allowSearch = false,
        placeholder,
        allowMulti = false,
        RenderInput,
        showArrow = false,
        onChangeSelectOpen = () => {},
    } = props;
    const [open, setOpen] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [search, setSearch] = useState<string>("");
    const split_list = value ? value.split(", ") : [];

    const updateValueForMulti = (item: string) => {
        if (split_list.includes(item)) {
            const filtered_list = split_list.filter((it) => it !== item);
            onChange?.(filtered_list.join(", "));
        } else {
            onChange([...split_list, item].join(", "));
        }
    };

    const updateValueDefault = (item: string) => {
        onChange?.(item);
        setOpen(false);
    };

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                position: relative;
            `}
            onBlur={() => {
                setOpen(false);
                onChangeSelectOpen(false);
            }}
        >
            {!RenderInput && (
                <InputContainer
                    css={[styles?.container]}
                    onClick={(e) => {
                        setOpen(!open);
                        onChangeSelectOpen(!open);
                        e.stopPropagation();
                    }}
                >
                    <p css={[styles?.text]}>{value ? value : placeholder}</p>
                    {showArrow && (
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                margin-left: auto;

                                rotate: ${open ? "180deg" : "0deg"};
                                transition: all ease 0.5s;
                            `}
                        >
                            <Icon name="top-arrow" />
                        </div>
                    )}
                </InputContainer>
            )}

            {RenderInput && (
                <InputContainer
                    css={css`
                        width: min-content;
                        background-color: transparent;
                        &:hover {
                            background-color: transparent;
                        }
                        padding: 0;
                    `}
                    onClick={(e) => {
                        setOpen(!open);
                        onChangeSelectOpen(!open);
                        e.stopPropagation();
                    }}
                >
                    {RenderInput}
                </InputContainer>
            )}

            {data.length > 0 && open && (
                <SelectContainer
                    css={[styles?.items]}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                        if (e.target === inputRef.current) {
                            onChangeSelectOpen(false);
                            setOpen(true);
                        }
                    }}
                >
                    {allowSearch && (
                        <InputField
                            ref={inputRef}
                            placeholder={"검색"}
                            styles={{
                                container: css`
                                    margin: 8px;
                                    width: calc(100% - 16px);

                                    z-index: 2;
                                `,
                            }}
                            onClick={() => {
                                if (allowSearch && inputRef.current) {
                                    inputRef.current.focus();
                                }
                            }}
                            onChange={(e) => {
                                if (e) setSearch(e);
                            }}
                        />
                    )}
                    {data
                        .filter((item) => item.toString().includes(search))
                        .map((item, index) => (
                            <SelectItem
                                css={[styles?.item]}
                                selected={allowMulti ? split_list.includes(item) : value === item}
                                key={index}
                                onClick={() => {
                                    if (allowMulti) {
                                        updateValueForMulti(item);
                                    } else {
                                        updateValueDefault(item);
                                    }
                                }}
                            >
                                {item}
                            </SelectItem>
                        ))}
                    {data
                        .filter((item) => !item.toString().includes(search))
                        .map((item, index) => (
                            <SelectItem
                                css={[styles?.item]}
                                selected={allowMulti ? split_list.includes(item) : value === item}
                                key={index}
                                onClick={() => {
                                    if (allowMulti) {
                                        updateValueForMulti(item);
                                    } else {
                                        updateValueDefault(item);
                                    }
                                }}
                            >
                                {item}
                            </SelectItem>
                        ))}
                </SelectContainer>
            )}
            {data.length === 0 && open && (
                <SelectContainer css={[styles?.items]} onMouseDown={(e) => e.preventDefault()}>
                    <SelectItem
                        selected={false}
                        css={css`
                            white-space: pre-wrap;
                            color: ${theme.neutral400};
                            &:hover {
                                background-color: white;
                            }
                        `}
                    >
                        선택할 수 있는 항목이 없습니다.
                    </SelectItem>
                </SelectContainer>
            )}
        </div>
    );
};

export default Select_V2;
